import * as React from "react";

/**
 * Determines if the content of an element is visually truncated.
 *
 * Utilizes a `ResizeObserver` to update the truncated state when the element's size changes.
 *
 * @param enabled – Set to `false` to pause observation (e.g., when the element is expanded).
 *                  The last known value is preserved while paused.
 */
export function useIsTruncated(enabled = true) {
  const [isTruncated, setIsTruncated] = React.useState(false);
  const enabledRef = React.useRef(enabled);
  enabledRef.current = enabled;

  const observerRef = React.useRef<ResizeObserver | null>(null);

  const ref = React.useCallback((el: HTMLElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    if (!el) {
      return;
    }

    const check = () => {
      if (enabledRef.current) {
        setIsTruncated(el.scrollHeight > el.clientHeight + 1);
      }
    };
    check();

    observerRef.current = new ResizeObserver(check);
    observerRef.current.observe(el);
  }, []);

  React.useEffect(() => {
    const obs = observerRef;
    return () => obs.current?.disconnect();
  }, []);

  return { ref, isTruncated };
}
